import { Dictionary, values, uniq, flatten } from 'lodash';
import { AugmentedDevice, Device, DeviceStatus } from 'shared/models';
import { TFunction } from 'i18next';
import { getSelectedKeys } from 'logic/getSelectedKeys';
import { expandMacAddress } from 'util/cleanMacAddress';
import { sortNames } from "logic/sortNames";

export type Node = {
    id: string | null;
    depth: number;
    label: string;
};

export type DeviceNode = Node & {
    device: Device;
};

export type GroupNode = Node & {
    filtered: boolean;
    numChildren: number;
    numMatchingChildren: number;
};

export type GroupBy =
    'none'
    | 'group'
    | 'network'
;

// Show down first, then very late, late, up, and disabled is last.
const getSortValueFromDeviceStatus = (s: DeviceStatus) => {
    if (!s) {
        return 2;
    }

    switch (s) {
        case DeviceStatus.Down: return -3;
        case DeviceStatus.Late2: return -2;
        case DeviceStatus.Late1: return -1;
        case DeviceStatus.Up: return 0;
        case DeviceStatus.Offline: return 1;
    }
};

const statusThenName = (a: DeviceNode, b: DeviceNode) => {
    const aDevice = a.device;
    const bDevice = b.device;

    const aStatusValue = getSortValueFromDeviceStatus(aDevice.status);
    const bStatusValue = getSortValueFromDeviceStatus(bDevice.status);
    if (aStatusValue !== bStatusValue) return aStatusValue - bStatusValue;

    const aName = aDevice.displayName || `_${aDevice.macAddress}` || `__${aDevice.id}`;
    const bName = bDevice.displayName || `_${bDevice.macAddress}` || `__${bDevice.id}`;
    return sortNames(aName, bName as string);
};

//
// Take a map of devices and turn into an array of nodes
//
export const buildTree = (
    deviceMap: Dictionary<AugmentedDevice>,
    groupBy: GroupBy,
    collapsed: Dictionary<boolean>,
    selectedTypes: Dictionary<boolean>,
    selectedStatuses: Dictionary<boolean>,
    t: TFunction,
): (DeviceNode | GroupNode)[] => {
    const filterOnTypes = getSelectedKeys(selectedTypes).length > 0;
    const filterOnStatuses = getSelectedKeys(selectedStatuses).length > 0;
    const filtered = filterOnTypes || filterOnStatuses;
    const devices = values(deviceMap);
    switch (groupBy) {
        case 'group':
            const groupNames = uniq(values(devices).map(d => d.groupName));
            return flatten(groupNames.map(groupName => {
                const groupId = groupName || 'null';
                const groupNode: GroupNode = {
                    id: groupId,
                    depth: 0,
                    label: groupName || t('DEVICES.NO_GROUP'),
                    filtered,
                    numChildren: 0,
                    numMatchingChildren: 0,
                };

                const children = devices.filter(d => d.groupName === groupName);
                const matchingChildren =
                    children
                        .filter(d => !filterOnTypes || selectedTypes[d.deviceTypeId])
                        .filter(d => !filterOnStatuses || selectedStatuses[d.status])
                        .map(d => ({
                            id: d.id,
                            depth: 1,
                            device: d,
                            label: d.displayName || expandMacAddress(d.macAddress) || d.id,
                        }) as DeviceNode)
                        .sort(statusThenName);

                // If we're filtering and haven't added any child nodes then also remove the group node
                if (matchingChildren.length === 0) {
                    return [];
                }

                // If none of the devices are grouped, just return the list of all devices at the root
                if (groupNames.length === 1 && groupNode.id === 'null') {
                    return matchingChildren.map(d => ({ ...d, depth: 0 }));
                }

                groupNode.numChildren = children.length;
                groupNode.numMatchingChildren = matchingChildren.length;

                return collapsed[groupId]
                    ? [ groupNode ]
                    : [ groupNode, ...matchingChildren ];
            }));

        case 'network':
            const networkNames = uniq(values(devices).map(d => d.networkName));
            return flatten(networkNames.map(networkName => {
                const groupId = networkName || 'null';
                const groupNode: GroupNode = {
                    id: groupId,
                    depth: 0,
                    label: networkName || t('DEVICES.PRIMARY_NETWORK'),
                    filtered,
                    numChildren: 0,
                    numMatchingChildren: 0,
                };

                const children = devices.filter(d => d.networkName === networkName);
                const matchingChildren =
                    children
                        .filter(d => !filterOnTypes || selectedTypes[d.deviceTypeId])
                        .filter(d => !filterOnStatuses || selectedStatuses[d.status])
                        .map(d => ({
                            id: d.id,
                            depth: 1,
                            device: d,
                            label: d.displayName || expandMacAddress(d.macAddress) || d.id,
                        }) as DeviceNode)
                        .sort(statusThenName);

                // If we're filtering and haven't added any child nodes then also remove the group node
                if (matchingChildren.length === 0) {
                    return [];
                }

                groupNode.numChildren = children.length;
                groupNode.numMatchingChildren = matchingChildren.length;

                return collapsed[groupId]
                    ? [ groupNode ]
                    : [ groupNode, ...matchingChildren ];
            }));

        case 'none':
            return devices
                .filter(d => !filterOnTypes || selectedTypes[d.deviceTypeId])
                .filter(d => !filterOnStatuses || selectedStatuses[d.status])
                .map(d => ({
                    id: d.id,
                    depth: 0,
                    device: d,
                    label: d.displayName || expandMacAddress(d.macAddress) || d.id,
                }))
                .sort(statusThenName);
    }
};
